import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import {
  addOrUpdateIfspOutcome,
  downloadIfspOutcomes,
  getIfsp,
  getIfspOutcome,
} from '../../../services/ifspClient/IfspApi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setIfspOutcomes } from '../../../redux/features/ifsp/ifspOutcomesSlice';
import ChildAndFamilyOutcomes from './ChildAndFamilyOutcomes';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { AlertColor } from '@mui/material';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import MediumTypography from '../../../components/formlib/MediumTypography';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import ModalPopup from '../../../components/formlib/ModalPopup';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import IfspHeld from '../ifspComponents/IfspHeld';
import { useOutcomeValidationCheck } from './ifspOutcomeValidtionCheck';
import { ReactComponent as DownloadIcon } from '../../../assets/images/downloadIcon.svg';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../../services/ApiResponseHandler';
import {
  updateIfspClientDetails,
  updateOriginalIfspClientDetails,
} from '../../../redux/features/ifsp/ifspClientDetailsSlice';
import { getClientDocumentFileName } from '../../../utils/fileUtils';

const IfspOutComeMainScreen = () => {
  const dispatch = useAppDispatch();
  const ifspOutComesDetails = useAppSelector((state) => state.ifspOutComes);
  const ifspClientDetails = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails,
  );
  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );
  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [isUnsavedPopupConfirmed, setIsUnsavedPopupConfirmed] =
    useState<boolean>(false);
  const [transitionRenderKey, setTransitionRenderKey] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [isMainOutComeSaveSuccessTrigger, setIsMainOutComeSaveSuccessTrigger] =
    useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { isOutcomeChanged } = useIfspUnsavedChanges();
  const parentRef = useRef<HTMLDivElement>(null);
  const validationError = useOutcomeValidationCheck();

  useEffect(() => {
    toggleLoader(true);
    getIfspOutcome()
      .then((res) => {
        toggleLoader(false);
        dispatch(
          setIfspOutcomes({
            ifspId: res.ifspId,
            outcome:
              res.outcome.length > 0
                ? res.outcome
                : res.outcome.map((item) => ({
                    ...item,
                    tempId: uuidv4(),
                  })),
            isCurrentlyHeld: res.isCurrentlyHeld,
            heldBy: res.heldBy,
            displayBanner: res.displayBanner,
          }),
        );
      })
      .catch(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.Outcome.GetError');
        setToastrDefaultMessage('Failed to get outcome data');
      });
  }, [ifspHeatBeatDetails.outcomeEmpId]);

  const getTbrFlagDetails = () => {
    const clientId = localStorage.getItem('ClientId');
    getIfsp(clientId as string)
      .then((data) => {
        const updatedDetails = {
          ...ifspClientDetails,
          tbrUpdateRequired: data.tbrUpdateRequired,
          outcomeTbrUpdateRequired: data.outcomeTbrUpdateRequired,
          serviceDeliveryTbrUpdateRequired:
            data.serviceDeliveryTbrUpdateRequired,
          transitionTbrUpdateRequired: data.transitionTbrUpdateRequired,
          isCommonAreaChecked: data.isCommonAreaChecked,
          isOutcomeChecked: data.isOutcomeChecked,
          isServiceDeliveryChecked: data.isServiceDeliveryChecked,
          isTransitionChecked: data.isTransitionChecked,
        };

        dispatch(updateIfspClientDetails(updatedDetails));
        dispatch(updateOriginalIfspClientDetails(updatedDetails));
      })
      .catch((error) => {
        toggleLoader(false);
        setOpen(true);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrVariable('error');
          setToastrId('IFSP.ClientDetails.GetError');
          setToastrDefaultMessage('Failed to get IFSP data');
        }
      });
  };

  const handleAddOrUpdate = () => {
    if (
      validationError.endDate.length > 0 ||
      validationError.outcome.length > 0 ||
      validationError.startDate.length > 0
    ) {
      setIsSaveClicked(true);
      setOpen(true);
      setToastrVariable('error');
      setToastrId('IFSP.SDPAndConsent.SubmitError');
      setToastrDefaultMessage(
        'Please correct the highlighted fields before submitting.',
      );
      setTimeout(() => {
        setOpen(false);
      }, 3000);
      return;
    } else {
      setIsSaveClicked(false);
    }
    toggleLoader(true);
    setIsMainOutComeSaveSuccessTrigger((prev) => !prev);
    addOrUpdateIfspOutcome({
      ifspId: ifspOutComesDetails.ifspId,
      outcome: ifspOutComesDetails.outcome,
      isCurrentlyHeld: ifspOutComesDetails.isCurrentlyHeld,
      heldBy: ifspOutComesDetails.heldBy,
      displayBanner: ifspOutComesDetails.displayBanner,
    })
      .then(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.Outcome.SaveSuccess');
        setToastrDefaultMessage('Outcome saved successfully');
        return getIfspOutcome()
          .then((res) => {
            dispatch(
              setIfspOutcomes({
                ifspId: res.ifspId,
                outcome: res.outcome,
                isCurrentlyHeld: res.isCurrentlyHeld,
                heldBy: res.heldBy,
                displayBanner: res.displayBanner,
              }),
            );
            return getTbrFlagDetails();
          })
          .catch(() => {
            toggleLoader(false);
            setOpen(true);
            setToastrVariable('error');
            setToastrId('IFSP.Outcome.GetError');
            setToastrDefaultMessage('Failed to get outcome data');
          });
      })
      .catch((error) => {
        setOpen(true);
        setToastrVariable('error');
        toggleLoader(false);
        const apiError = error as ApiMessage;
        if (apiError.code === 1162 || apiError.code === 1174) {
          setToastrId(apiError.message);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('IFSP.Outcome.SaveError');
          setToastrDefaultMessage('Failed to save outcome');
        }
      });
  };

  const handleDownload = () => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const localTimezone = dayjs.tz.guess();
    if (clientId) {
      downloadIfspOutcomes(clientId, localTimezone)
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });

          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `Individualized Family Service Plan (IFSP).pdf`,
          ); // Set desired file name here

          // Append anchor to document body
          document.body.appendChild(anchor);

          // Trigger anchor click
          anchor.click();

          window.open(blobUrl, '_blank');

          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);

          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };

  const handleCloseSnakBar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  return (
    <Box sx={{ backgroundColor: '#E4EEEE' }}>
      {ifspOutComesDetails.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspOutComesDetails?.heldBy} />
        </Box>
      ) : null}

      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspOutComesDetails.isCurrentlyHeld ? 'none' : 'auto',
          opacity: ifspOutComesDetails.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspOutComesDetails.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspOutComesDetails.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <SnackBarComponent
          open={open}
          handleClose={handleCloseSnakBar}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        <ModalPopup
          open={openUnSavedModal}
          description="formUnsavedChangesMessage"
          onCancel={() => {
            setOpenUnSavedModal(false);
          }}
          onOk={() => {
            dispatch(
              setIfspOutcomes({
                ifspId: ifspOutComesDetails.ifspId,
                outcome: ifspOutComesDetails.originalOutcome,
                isCurrentlyHeld: ifspOutComesDetails.isCurrentlyHeld,
                heldBy: ifspOutComesDetails.heldBy,
                displayBanner: ifspOutComesDetails.displayBanner,
              }),
            );
            setIsUnsavedPopupConfirmed((prev) => !prev);
            setTransitionRenderKey((prev) => prev + 1);
            setTimeout(() => {
              setOpenUnSavedModal(false);
            }, 500);
          }}
          labelId1="Clientpage.cancelbtn"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="Ok"
        />
        <Box className="flex__ justifyContentSpaceBtw alignItemCenter pr-xlg">
          <MediumTypography
            labelid="IFSP.Outcome"
            defaultlabel="Outcomes"
            fontSize="18px"
            fontweight={600}
            className="pl-md pt-md"
          />
          <DownloadIcon
            className="cursorPointer mt-md"
            onClick={handleDownload}
          />
        </Box>
        <ChildAndFamilyOutcomes
          key={transitionRenderKey}
          isSaveClicked={isSaveClicked}
          isMainOutComeSaveSuccessTrigger={isMainOutComeSaveSuccessTrigger}
          isUnsavedPopupConfirmed={isUnsavedPopupConfirmed}
        />
        <Box
          component="div"
          className="flex__ justifyContent-FlexEnd pt-lg pb-lg pr-md"
        >
          <ButtonComponent
            className="btn-primary btn-cancel"
            labelId={'cancelText'}
            defaultLabelId="Cancel"
            variantType="outlined"
            onClick={() => {
              if (
                isOutcomeChanged ||
                ifspUnsavedFields.isOutComeProgressInfoFieldsChange
              ) {
                setOpenUnSavedModal(true);
              } else {
                setOpenUnSavedModal(false);
              }
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
          <ButtonComponent
            className="btn-primary btn-submit  ml-md"
            labelId="IFSP.SaveOutcomes"
            defaultLabelId="Save Outcomes"
            variantType="contained"
            onClick={async () => {
              handleAddOrUpdate();
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default IfspOutComeMainScreen;
